import startCase from "lodash.startcase";
import { Search } from "lucide-react";
import React, { useMemo } from "react";
import { Updater } from "use-immer";

import { IgSelect, IgTextField } from "@intelligems/ig-theme";
import { OrganizationSummaryDto } from "@intelligems/ig-types";
import { Grid, MenuItem } from "@mui/material";
import { OrganizationOverviewFilter } from "@src/features/admin/pages/overview/components/Tables/OrganizationOverviewTable/types";

interface SearchBarProps {
  filters: OrganizationOverviewFilter;
  setFilters: Updater<OrganizationOverviewFilter>;
  orgs: OrganizationSummaryDto[];
}
export const SearchBar = ({ filters, setFilters, orgs }: SearchBarProps) => {
  const dropdowns = useMemo<
    {
      key: keyof OrganizationOverviewFilter;
      values: string[];
      all: string;
    }[]
  >(() => {
    const statuses = Array.from(
      new Set(
        orgs.flatMap((d) => {
          return [
            ...d.stripeSubscriptionSummaries.map((s) => s.status),
            ...d.organizationSubscriptions.map((os) => os.status),
          ];
        })
      )
    );

    const billingIntervals = Array.from(
      new Set(
        orgs.flatMap((d) => {
          return [
            ...d.stripeSubscriptionSummaries.map((s) => s.interval),
            ...d.organizationSubscriptions.map((os) => os.interval!),
          ];
        })
      )
    );

    const billingProviders = Array.from(
      new Set(
        orgs.flatMap((d) => {
          return [
            ...d.stripeSubscriptionSummaries.map((s) => s.billingProvider),
            ...d.organizationSubscriptions.map((os) => os.billingProvider),
          ];
        })
      )
    );

    return [
      {
        key: "billingProvider",
        values: billingProviders,
        all: "All Providers",
      },
      {
        key: "interval",
        values: billingIntervals,
        all: "All Intervals",
      },
      {
        key: "status",
        values: statuses,
        all: "All Statuses",
      },
    ];
  }, [orgs]);

  return (
    <Grid
      container
      item
      justifyContent={"space-between"}
      columnSpacing={1}
      alignItems={"center"}
    >
      <Grid item flex={1}>
        <IgTextField
          fullWidth={true}
          sx={{
            backgroundColor: "white",
          }}
          placeholder={"Enter Customer Name, Shop ID, or ID"}
          value={filters.search}
          onChange={(e) => {
            setFilters((draft) => {
              draft.search = e.target.value;
            });
          }}
          InputProps={{
            endAdornment: <Search size={16} />,
          }}
        />
      </Grid>
      {dropdowns.map((dd) => (
        <Grid item key={dd.key}>
          <IgSelect
            variant={"outlined"}
            value={filters[dd.key]}
            onChange={(e) => {
              setFilters((draft) => {
                draft[dd.key] = `${e.target.value}`;
                return draft;
              });
            }}
          >
            <MenuItem value={"all"}>{dd.all}</MenuItem>
            {dd.values.map((v) => (
              <MenuItem key={v} value={v}>
                {startCase(`${v}`)}
              </MenuItem>
            ))}
          </IgSelect>
        </Grid>
      ))}
    </Grid>
  );
};
